import { Button } from 'Components/shared/Button';
import { Heading } from 'Components/shared/Heading';
import { usePreferences } from 'Hooks/preferences/usePreferences'; // Import the hook
import { MobXProviderContext, observer } from 'mobx-react';
import { useSoftphoneCoordinatorStore } from 'Modules/softphone/store';
import React from 'react';
import type { RootStoreProps } from 'Stores/RootStore.types';
import { pushToGTMDataLayer } from 'Utils/analytics';
import { useShallow } from 'zustand/react/shallow';
import { SwitchWithLabel } from '~/lib/ui/switchWithLabel/index';
import { Styled } from './index.styles';

export const NotificationsSettings: React.FC = observer(() => {
  const testid = 'notificationSettings';
  const { ui, notification, phoneCall, person } =
    React.useContext<RootStoreProps>(MobXProviderContext);
  const { preferences, preferencesQuery, updatePreferences } = usePreferences();

  // Get softphone coordinator store for the current user
  const personId = person?.loggedInPersonId;
  const softphoneCoordinatorStore = useSoftphoneCoordinatorStore(personId);

  // Zustand store values (for new softphone)
  const { isNewSoftphoneEnabled, enableIncomingCalls: enableIncomingCallsNew, toggleIncomingCalls: toggleIncomingCallsNew } =
    softphoneCoordinatorStore(
      useShallow((state) => ({
        isNewSoftphoneEnabled: state.isNewSoftphoneEnabled,
        enableIncomingCalls: state.enableIncomingCalls,
        toggleIncomingCalls: state.toggleIncomingCalls,
      }))
    );

   // Legacy values (from PhoneStore MobX)
   const enableIncomingCallsLegacy = phoneCall?.enableIncomingCalls ?? true;

   // Final value based on which softphone is enabled
   const enableIncomingCalls = isNewSoftphoneEnabled
     ? enableIncomingCallsNew
     : enableIncomingCallsLegacy;

  React.useEffect(() => {
    void ui.getOnlyMentions();
    if (!isNewSoftphoneEnabled) {
      phoneCall.updateEnableIncomingCalls();
    }
  }, [isNewSoftphoneEnabled, phoneCall, ui]);

  if (preferencesQuery.isPending) return <div>Loading...</div>;
  if (preferencesQuery.isError) return <div>Error loading preferences</div>;

  const onToggleShowOnlyDirectMentions = (checked: boolean) => {
    pushToGTMDataLayer('toggleShowOnlyDirectMentions');

    updatePreferences.mutate({
      directMentionsOnly: checked,
    });
  };

  const onToggleShowIncomingCalls = (checked: boolean) => {
    if (isNewSoftphoneEnabled) {
      toggleIncomingCallsNew(checked);
    } else {
      phoneCall.setEnableIncomingCalls(checked);
    }
    pushToGTMDataLayer('toggleShowIncomingCalls');
  };

  const onToggleDesktopNotifications = (checked: boolean) => {
    // If the Prompt (at the top of the app) is still showing, dismiss it
    notification.setWebNotificationPromptActionTaken(true);

    const wasEnabled = checked
      ? notification.tryEnableWebNotifications()
      : notification.setWebNotificationsEnabled(false);

    pushToGTMDataLayer(
      `settings_notifications_desktop_notifications_${wasEnabled ? 'on' : 'off'
      }`
    );
  };

  const onToggleNotificationAudio = (checked: boolean) => {
    updatePreferences.mutate({
      notificationAudio: checked,
    });
  };

  const onTestDesktopNotificationClick = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    event.preventDefault();
    notification.addWebNotification(
      'Test notification',
      {
        tag: 'test:test-desktop-notif',
        silent: true,
        body: 'This is a test notification. If you see this, desktop notifications are working.',
        icon: 'https://www.gravatar.com/avatar/?d=mm',
        timeout: 3000,
      },
      false,
      true
    );
  };

  return (
    <div className="settings-notifications">
      <div>
        <Heading variant="h2">Notifications</Heading>

        <div className="flex flex-col gap-9 mt-9">
          <SwitchWithLabel
            id="directMentionsOnly"
            label="Enable only direct mentions (includes @here)"
            checked={preferences.directMentionsOnly}
            onChange={onToggleShowOnlyDirectMentions}
            helperText="Enable group notification only if you are explicitly mentioned"
            testid={`${testid}-toggleButtonEnableOnlyDirectMentions`}
          />

          <SwitchWithLabel
            id="enableIncomingCalls"
            label="Enable incoming calls"
            checked={enableIncomingCalls}
            onChange={onToggleShowIncomingCalls}
            testid={`${testid}-toggleButtonEnableIncomingCalls`}
          />

          <SwitchWithLabel
            id="notificationAudio"
            label="Enable notification sounds"
            checked={preferences.notificationAudio}
            onChange={onToggleNotificationAudio}
            testid={`${testid}-toggleButtonEnableNotificationSounds`}
          />

          <SwitchWithLabel
            id="webNotificationsEnabled"
            label="Enable desktop notifications"
            checked={notification.webNotificationsEnabled}
            onChange={onToggleDesktopNotifications}
            helperText="Allows you to receive notifications for messages, calls and video calls while you’re not actively using the app."
            testid={`${testid}-toggleButtonEnableDesktopNotifications`}
          />
        </div>

        {notification.webNotificationsEnabled ? (
          <Styled.NotificationNotice>
            <Styled.InfoIcon icon="info-filled" />
            <Styled.NotificationNoticeHighlight>
              Please notice:
            </Styled.NotificationNoticeHighlight>
            &nbsp;to trigger notifications, ensure that you also grant
            permission for both browser and system notifications
          </Styled.NotificationNotice>
        ) : null}
      </div>
      <Button
        variant="primary"
        disabled={!notification.webNotificationsEnabled}
        content="Test desktop notifications"
        onClick={onTestDesktopNotificationClick}
        testid={`${testid}-buttonTestDesktopNotifications`}
      />
    </div>
  );
});
